import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Guest, MealChoice, RsvpRequest } from '../types';
import './Dashboard.css';

const Dashboard: React.FC = () => {
  const [guest, setGuest] = useState<Guest | null>(null);
  const [meals, setMeals] = useState<MealChoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    rsvpStatus: 'attending' as 'attending' | 'declined',
    mealId: '',
    allergies: '',
    note: '',
    plusOneFirstName: '',
    plusOneLastName: '',
    wants_plus_one: false,
    plusOneMealId: '',
    plusOneAllergies: '',
    plusOneRsvpStatus: 'attending' as 'attending' | 'declined',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [guestResponse, mealsResponse] = await Promise.all([
        api.get<Guest>('/guest/profile'),
        api.get<MealChoice[]>('/meals'),
      ]);

      setGuest(guestResponse.data);
      setMeals(mealsResponse.data);

      // Pre-fill form if guest has already responded
      if (guestResponse.data.rsvpStatus !== 'pending') {
        const existingPlusOne = guestResponse.data.plusOne;
        setFormData({
          rsvpStatus: guestResponse.data.rsvpStatus as 'attending' | 'declined',
          mealId: guestResponse.data.mealId || '',
          allergies: guestResponse.data.allergies || '',
          note: guestResponse.data.note || '',
          plusOneFirstName: existingPlusOne?.firstName || '',
          plusOneLastName: existingPlusOne?.lastName || '',
          wants_plus_one: false,
          plusOneMealId: existingPlusOne?.mealId || '',
          plusOneAllergies: existingPlusOne?.allergies || '',
          plusOneRsvpStatus: (existingPlusOne?.rsvpStatus as 'attending' | 'declined') || 'attending',
        });
      }
    } catch (err) {
      setError('Échec du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const rsvpData: RsvpRequest = {
        rsvp_status: formData.rsvpStatus,
        meal_id: formData.rsvpStatus === 'attending' ? formData.mealId : undefined,
        allergies: formData.allergies || undefined,
        note: formData.note || undefined,
        wants_plus_one: formData.wants_plus_one,
      };

      // Add plus one fields if creating a new plus one or updating existing one
      if (formData.wants_plus_one && !guest?.plusOne && formData.rsvpStatus === 'attending') {
        rsvpData.plus_one_first_name = formData.plusOneFirstName || undefined;
        rsvpData.plus_one_last_name = formData.plusOneLastName || undefined;
        rsvpData.plus_one_meal_id = formData.plusOneMealId || undefined;
        rsvpData.plus_one_allergies = formData.plusOneAllergies || undefined;
      } else if (guest?.plusOne) {
        // Always send plus-one data for known plus-ones (they can attend independently)
        // For anonymous plus-ones, only send when main guest is attending
        const isKnownPlusOne = !!(guest.plusOne.firstName || guest.plusOne.lastName);
        if (isKnownPlusOne || formData.rsvpStatus === 'attending') {
          rsvpData.plus_one_rsvp_status = formData.plusOneRsvpStatus;
          rsvpData.plus_one_first_name = formData.plusOneFirstName || undefined;
          rsvpData.plus_one_last_name = formData.plusOneLastName || undefined;
          rsvpData.plus_one_meal_id = formData.plusOneRsvpStatus === 'attending' ? (formData.plusOneMealId || undefined) : undefined;
          rsvpData.plus_one_allergies = formData.plusOneRsvpStatus === 'attending' ? (formData.plusOneAllergies || undefined) : undefined;
        }
      }

      console.log('Submitting RSVP data:', rsvpData);

      await api.put('/guest/rsvp', rsvpData);
      setSuccess('RSVP mis à jour avec succès !');
      await fetchData();
    } catch (err) {
      console.error('RSVP submission error:', err);
      setError('Échec de la mise à jour du RSVP. Veuillez réessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="loading">Chargement...</div>;
  }

  if (!guest) {
    return <div className="error">Invité non trouvé</div>;
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-card">
        <h1 className="dashboard-title">RSVP pour notre mariage</h1>
        <p className="dashboard-subtitle">3 octobre 2026</p>

        {guest.rsvpStatus === "attending" && (
          <div className="guest-info">
            <p className="plus-one-note">Merci d'avoir confirmé votre présence.</p><p className="plus-one-note">Si vous souhaitez apporter des modifications, veuillez nous contacter au :<br/><br/><a href="mailto:ml@ja-dev.com">ml@ja-dev.com</a></p>
            <p className="info-text">Merci d'envoyer votre paiement de <strong>{(guest.plusOne && guest.plusOne.rsvpStatus === 'attending') ? 170 : 85}$</strong> au <strong>ml@ja-dev.com</strong></p>
          </div>
        )}

        <div className="guest-info">
          <h2>Invité(s)</h2>
          <h3>{guest.firstName} {guest.lastName}
          <span className={`status-badge status-${guest.rsvpStatus}`}>
            {guest.rsvpStatus === 'pending' ? 'Non répondu' : guest.rsvpStatus === 'attending' ? 'Présent' : 'Absent'}
          </span></h3>

          {guest.rsvpStatus === "attending" && (
            <>
              <p><strong>Choix du repas :</strong> {guest.meal ? guest.meal.title : ""}</p>
              <p><strong>Restrictions alimentaires :</strong> {guest.allergies}</p>
            </>
          )}

          {guest.plusOne && (
            <div className="plus-one-guest-info">
              <h3>{guest.plusOne.firstName ? `${guest.plusOne.firstName} ${guest.plusOne.lastName}` : "Accompagnateur/Accompagnatrice"}
              <span className={`status-badge status-${guest.plusOne.rsvpStatus}`}>
                {guest.plusOne.rsvpStatus === 'pending' ? 'Non répondu' : guest.plusOne.rsvpStatus === 'attending' ? 'Présent' : 'Absent'}
              </span></h3>

              {guest.plusOne.rsvpStatus === "attending" && (
              <>
                <p className="plus-one-detail">
                  <strong>Choix du repas:</strong> {guest.plusOne.meal ? guest.plusOne.meal.title : ""}
                </p>
                <p className="plus-one-detail">
                  <strong>Restrictions alimentaires:</strong> {guest.plusOne.allergies}
                </p>
              </>
              )}
            </div>
          )}
        </div>

        {guest.rsvpStatus !== "pending" && (
          <div className="guest-info">
            <p><strong>Note :</strong> {guest.note}</p>
          </div>
        )}

        {guest.rsvpStatus === 'pending' && (
        <form onSubmit={handleSubmit} className="rsvp-form">
          <div className="form-section">
            <h3>Serez-vous présent(e) ?</h3>
            <div className="radio-group">
              <label className="radio-label">
                <input
                  type="radio"
                  name="rsvpStatus"
                  value="attending"
                  checked={formData.rsvpStatus === 'attending'}
                  onChange={(e) => setFormData({ ...formData, rsvpStatus: 'attending' })}
                />
                <span>Oui, je serai présent(e) !</span>
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="rsvpStatus"
                  value="declined"
                  checked={formData.rsvpStatus === 'declined'}
                  onChange={(e) => setFormData({ ...formData, rsvpStatus: 'declined', wants_plus_one: false })}
                />
                <span>Désolé(e), je ne peux pas venir</span>
              </label>
            </div>
          </div>

          {formData.rsvpStatus === 'attending' && (
            <>
              <div className="form-section">
                <h3>Choix du repas</h3>
                <div className="meal-choices">
                  {meals.map((meal) => (
                    <label key={meal.id} className="meal-option">
                      <input
                        type="radio"
                        name="mealId"
                        value={meal.id}
                        checked={formData.mealId === meal.id}
                        onChange={(e) => setFormData({ ...formData, mealId: e.target.value })}
                        required={formData.rsvpStatus === 'attending'}
                      />
                      <div className="meal-details">
                        <strong>{meal.title}</strong>
                        <p>{meal.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-section">
                <label htmlFor="allergies">Restrictions alimentaires / Allergies</label>
                <textarea
                  id="allergies"
                  value={formData.allergies}
                  onChange={(e) => setFormData({ ...formData, allergies: e.target.value })}
                  placeholder="Merci de nous indiquer vos allergies ou restrictions alimentaires"
                  rows={3}
                  className="form-textarea"
                />
              </div>

              {/* Plus One Section - existing plus one from database (anonymous - only shown when attending) */}
              {guest.plusOne && !guest.plusOne.firstName && !guest.plusOne.lastName && (
                <div className="form-section">
                  <h3>Accompagnateur/Accompagnatrice</h3>

                  <p className="section-description">Sera-t-il/elle présent(e) ?</p>
                  <div className="radio-group">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="plusOneRsvpStatus"
                        value="attending"
                        checked={formData.plusOneRsvpStatus === 'attending'}
                        onChange={() => setFormData({ ...formData, plusOneRsvpStatus: 'attending' })}
                      />
                      <span>Oui, il/elle sera présent(e)</span>
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="plusOneRsvpStatus"
                        value="declined"
                        checked={formData.plusOneRsvpStatus === 'declined'}
                        onChange={() => setFormData({ ...formData, plusOneRsvpStatus: 'declined', plusOneMealId: '', plusOneAllergies: '' })}
                      />
                      <span>Non, il/elle ne pourra pas venir</span>
                    </label>
                  </div>

                  {formData.plusOneRsvpStatus === 'attending' && (
                    <>
                      <div className="plus-one-fields">
                        <div className="form-group">
                          <label htmlFor="plusOneFirstName">Prénom de l'accompagnateur/Accompagnatrice</label>
                          <input
                            type="text"
                            id="plusOneFirstName"
                            value={formData.plusOneFirstName}
                            onChange={(e) => setFormData({ ...formData, plusOneFirstName: e.target.value })}
                            placeholder="Prénom"
                            className="form-input"
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor="plusOneLastName">Nom de l'accompagnateur/Accompagnatrice</label>
                          <input
                            type="text"
                            id="plusOneLastName"
                            value={formData.plusOneLastName}
                            onChange={(e) => setFormData({ ...formData, plusOneLastName: e.target.value })}
                            placeholder="Nom"
                            className="form-input"
                            required
                          />
                        </div>
                      </div>

                      <div className="plus-one-meal-section">
                        <h4>Choix du repas</h4>
                        <div className="meal-choices">
                          {meals.map((meal) => (
                            <label key={meal.id} className="meal-option">
                              <input
                                type="radio"
                                name="plusOneMealId"
                                value={meal.id}
                                checked={formData.plusOneMealId === meal.id}
                                onChange={(e) => setFormData({ ...formData, plusOneMealId: e.target.value })}
                                required
                              />
                              <div className="meal-details">
                                <strong>{meal.title}</strong>
                                <p>{meal.description}</p>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor="plusOneAllergies">Restrictions alimentaires / Allergies</label>
                        <textarea
                          id="plusOneAllergies"
                          value={formData.plusOneAllergies}
                          onChange={(e) => setFormData({ ...formData, plusOneAllergies: e.target.value })}
                          placeholder="Merci de nous indiquer les allergies ou restrictions alimentaires de votre accompagnateur/accompagnatrice"
                          rows={3}
                          className="form-textarea"
                        />
                      </div>
                    </>
                  )}
                </div>
              )}
            </>
          )}

          {/* Plus One Section - known plus one (independent of main guest status) */}
          {guest.plusOne && guest.plusOne.firstName && guest.plusOne.lastName && (
            <div className="form-section">
              <h3>{guest.plusOne.firstName} {guest.plusOne.lastName}</h3>

              <p className="section-description">Sera-t-il/elle présent(e) ?</p>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="plusOneRsvpStatus"
                    value="attending"
                    checked={formData.plusOneRsvpStatus === 'attending'}
                    onChange={() => setFormData({ ...formData, plusOneRsvpStatus: 'attending' })}
                  />
                  <span>Oui, il/elle sera présent(e)</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="plusOneRsvpStatus"
                    value="declined"
                    checked={formData.plusOneRsvpStatus === 'declined'}
                    onChange={() => setFormData({ ...formData, plusOneRsvpStatus: 'declined', plusOneMealId: '', plusOneAllergies: '' })}
                  />
                  <span>Non, il/elle ne pourra pas venir</span>
                </label>
              </div>

              {formData.plusOneRsvpStatus === 'attending' && (
                <>
                  <div className="plus-one-meal-section">
                    <h4>Choix du repas</h4>
                    <div className="meal-choices">
                      {meals.map((meal) => (
                        <label key={meal.id} className="meal-option">
                          <input
                            type="radio"
                            name="plusOneMealId"
                            value={meal.id}
                            checked={formData.plusOneMealId === meal.id}
                            onChange={(e) => setFormData({ ...formData, plusOneMealId: e.target.value })}
                            required
                          />
                          <div className="meal-details">
                            <strong>{meal.title}</strong>
                            <p>{meal.description}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="plusOneAllergies">Restrictions alimentaires / Allergies</label>
                    <textarea
                      id="plusOneAllergies"
                      value={formData.plusOneAllergies}
                      onChange={(e) => setFormData({ ...formData, plusOneAllergies: e.target.value })}
                      placeholder="Merci de nous indiquer les allergies ou restrictions alimentaires"
                      rows={3}
                      className="form-textarea"
                    />
                  </div>
                </>
              )}
            </div>
          )}

          <div className="form-section">
            <label htmlFor="note">Notes supplémentaires</label>
            <textarea
              id="note"
              value={formData.note}
              onChange={(e) => setFormData({ ...formData, note: e.target.value })}
              placeholder="Des demandes spéciales ou des messages pour nous ?"
              rows={3}
              className="form-textarea"
            />
          </div>

          {error && <div className="message error-message">{error}</div>}
          {success && <div className="message success-message">{success}</div>}

          <button type="submit" disabled={submitting} className="submit-btn">
            {submitting ? 'Envoi en cours...' : 'Envoyer le RSVP'}
          </button>
        </form>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
